import { Model } from 'app/model/backendmodel';
import { TriggerSettings } from 'app/model/trigger/trigger-settings';
import {
    AdapterRegistry,
    BaseAsyncAdapter,
    EventTriggerProperties,
    IModel,
    ITridentResource,
    Metamodel,
    Owned,
    stringsAreEqualCaseInsensitive,
    TriggerEvent,
    TriggerProperties,
    ValueAdapter
} from 'shell-models-lib';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(TriggerSettings),
    asynchronous: true
})
export class TriggerSettingsAdapter extends BaseAsyncAdapter<TriggerSettings, Model.Trigger> {
    protected async _toResource(trigger: TriggerSettings): Promise<Model.Trigger> {
        const result: Model.Trigger = { type: undefined };
        result.runtimeState = trigger.isActivated ? 'Started' : 'Stopped';

        if (!trigger.unknownType) {
            if (Metamodel.modelOf(trigger.typeProperties) === EventTriggerProperties.MODEL) {
                result.type = EventTriggerProperties.MODEL.name;
                (result as Model.BlobEventsTrigger).typeProperties = this.blobEventsTriggerToResource(
                    trigger.typeProperties as EventTriggerProperties
                );
            }
            // currently only storage and schedule trigger is supported in trident (schedule is separate artifact), but we can keep the following in case we need to support more triggers.
            // else if (Metamodel.modelOf(trigger.properties) === TumblingWindowTriggerProperties.MODEL) {
            //     result.type = TumblingWindowTriggerProperties.MODEL.name;
            //     (result as Model.TumblingWindowTrigger).typeProperties = this.tumblingWindowTriggerToResource(trigger.properties as TumblingWindowTriggerProperties, trigger.name);
            // } else if (Metamodel.modelOf(trigger.properties) === ChainingTriggerProperties.MODEL) {
            //     result.type = ChainingTriggerProperties.MODEL.name;
            //     (result as Model.ChainingTrigger).typeProperties = this.chainingTriggerToResource(trigger.properties as ChainingTriggerProperties);
            // } else if (Metamodel.modelOf(trigger.properties) === RerunTumblingWindowTriggerProperties.MODEL) {
            //     result.type = RerunTumblingWindowTriggerProperties.MODEL.name;
            //     (result as Model.RerunTumblingWindowTrigger).typeProperties = this.rerunTumblingWindowTriggerToResource(trigger.properties as RerunTumblingWindowTriggerProperties);
            // } else if (Metamodel.modelOf(trigger.properties) === CustomEventsTriggerProperties.MODEL) {
            //     result.type = CustomEventsTriggerProperties.MODEL.name;
            //     (result as Model.CustomEventsTrigger).typeProperties = this.customEventsTriggerToResource(trigger.properties as CustomEventsTriggerProperties);
            // }
        } else {
            if (trigger.raw) {
                const parsed = JSON.parse(trigger.raw);
                _.merge(result, parsed);
            }
        }
        return result;
    }

    protected async _toModel(modelTriggerResource: Model.Trigger, trigger?: TriggerSettings): Promise<TriggerSettings> {
        if (!trigger) {
            throw new Error('Trigger settings should be initialized before calling toModel');
        }
        if (modelTriggerResource) {
            trigger.isActivated = stringsAreEqualCaseInsensitive(modelTriggerResource.runtimeState, 'Started');

            if (modelTriggerResource.type === EventTriggerProperties.MODEL.name) {
                const typeProperties = (modelTriggerResource as Model.BlobEventsTrigger).typeProperties;
                if (typeProperties) {
                    trigger.createBlobEventsTrigger(
                        typeProperties.scope as string,
                        typeProperties.tenantId,
                        typeProperties.events as TriggerEvent[],
                        typeProperties.blobPathBeginsWith,
                        typeProperties.blobPathEndsWith,
                        typeProperties.ignoreEmptyBlobs,
                        typeProperties.maxConcurrency,
                        typeProperties.dmtsConnectionId,
                        typeProperties.storageAccountName);
                } else {
                    trigger.typeProperties = Owned.create<EventTriggerProperties>(
                        EventTriggerProperties.MODEL,
                        trigger
                    );
                }
            }
            // else if (modelTriggerResource.type === TumblingWindowTriggerProperties.MODEL.name) {
            //     const typeProperties = (modelTriggerResource as Model.TumblingWindowTrigger).typeProperties;
            //     let dependsOn: TriggerDependency[];
            //     if (typeProperties) {
            //         if (typeProperties.dependsOn) {
            //             dependsOn = this.getTriggerReferencesToTumblingWindow(typeProperties.dependsOn, trigger.name);
            //         }
            //         // Add 'Z' suffix if not already there
            //         const startTimeUTC = typeProperties.startTime.endsWith('Z') ?
            //             new Date(typeProperties.startTime) :
            //             new Date(`${typeProperties.startTime}Z`);
            //         const endTimeUTC = typeProperties.endTime ? typeProperties.endTime.endsWith('Z') ?
            //             new Date(typeProperties.endTime) :
            //             new Date(`${typeProperties.endTime}Z`) : undefined;
            //         trigger.createTumblingWindowTrigger(typeProperties.frequency as TriggerFrequency, startTimeUTC, typeProperties.interval as number,
            //             typeProperties.delay, typeProperties.maxConcurrency as number, endTimeUTC, this.retryPolicyToModel(typeProperties.retryPolicy), dependsOn);
            //     } else {
            //         trigger.properties = new TumblingWindowTriggerProperties();
            //     }
            // } else if (modelTriggerResource.type === CustomEventsTriggerProperties.MODEL.name) {
            //     const typeProperties = (modelTriggerResource as Model.CustomEventsTrigger).typeProperties;
            //     if (typeProperties) {
            //         trigger.createCustomEventsTrigger(typeProperties.scope as string, typeProperties.tenantId, typeProperties.events as string[],
            //             typeProperties.subjectBeginsWith, typeProperties.subjectEndsWith, typeProperties.advancedFilters);
            //     } else {
            //         trigger.properties = new CustomEventsTriggerProperties();
            //     }
            // } else if (modelTriggerResource.type === ChainingTriggerProperties.MODEL.name) {
            //     const typeProperties = (modelTriggerResource as Model.ChainingTrigger).typeProperties;
            //     if (typeProperties) {
            //         trigger.createChainingTrigger(typeProperties.dependsOn.map((pipelineReference: Model.PipelineReference) =>
            //             ReferenceAdapter.toProxyModel<Pipeline>(Metamodel.modelFor(Pipeline), pipelineReference as Model.PipelineReference)),
            //             typeProperties.runDimension);
            //     } else {
            //         trigger.properties = new ChainingTriggerProperties();
            //     }
            // } else if (modelTriggerResource.type === RerunTumblingWindowTriggerProperties.MODEL.name) {
            //     const typeProperties = (modelTriggerResource as Model.RerunTumblingWindowTrigger).typeProperties;
            //     typeProperties.requestedStartTime = new Date(typeProperties.requestedStartTime);
            //     typeProperties.requestedEndTime = new Date(typeProperties.requestedEndTime);
            //     if (typeProperties) {
            //         trigger.createRerunRangeTumblingWindowTrigger(typeProperties.parentTrigger.referenceName, typeProperties.requestedStartTime,
            //             typeProperties.requestedEndTime, typeProperties.rerunConcurrency);
            //     } else {
            //         trigger.properties = new RerunTumblingWindowTriggerProperties();
            //     }
            // }
            else {
                let model: IModel | undefined;
                try {
                    model = Metamodel.modelByName(modelTriggerResource.type);
                } catch {
                    model = Metamodel.createModel(TriggerProperties.MODEL, {
                        name: modelTriggerResource.type,
                        display: [modelTriggerResource.type],
                        createdFromUnknownType: true
                    });
                }

                trigger.typeProperties = model.create();
                trigger.unknownType = true;
                trigger.raw = JSON.stringify(
                    await ValueAdapter.remainderWithExternalSpec(this.context, modelTriggerResource, {
                        name: '*',
                        typeProperties: {
                            description: '*',
                            runtimeState: '*',
                            pipelines: '*'
                        }
                    })
                );
            }
        }

        return trigger;
    }

    protected async _toAzureResource(tridentResource: ITridentResource): Promise<Model.Trigger> {
        throw `Artifact type '${tridentResource.artifactType}' does not implement _toAzureResource method.`;
    }

    // private getTumblingWindowReferences(typeProperties: TumblingWindowTriggerProperties, parentTriggerName: string): Model.TriggerDependency[] {
    //     let dependsOn: Model.TriggerDependency[] = [];
    //     if (typeProperties.dependsOn) {
    //         dependsOn = typeProperties.dependsOn.map((item) => {
    //             let triggerReference: Model.TriggerReference;
    //             const triggerDependency: Model.TriggerDependency = {
    //                 type: 'SelfDependencyTumblingWindowTriggerReference',
    //                 size: item.windowSize ? item.windowSize : undefined,
    //                 offset: item.offset ? item.offset : undefined
    //             };
    //             if (item.trigger && item.trigger.name !== parentTriggerName) {
    //                 triggerReference = ReferenceAdapter.toResource(item.trigger, this.context) as Model.TriggerReference;
    //                 triggerDependency.referenceTrigger = triggerReference;
    //                 triggerDependency.type = 'TumblingWindowTriggerDependencyReference';
    //             }
    //             return triggerDependency;
    //         }).filter((item) => item !== undefined);
    //     }
    //     return dependsOn;
    // }

    // private tumblingWindowTriggerToResource(properties: TumblingWindowTriggerProperties, parentTriggerName: string): Model.TumblingWindowTriggerTypeProperties {
    //     let retryPolicy: Model.RetryPolicy = {};
    //     if (properties.retryPolicy) {
    //         retryPolicy = {
    //             count: properties.retryPolicy.count,
    //             intervalInSeconds: properties.retryPolicy.interval
    //         };
    //     }
    //     let dependsOn: Model.TriggerDependency[];
    //     if (properties.dependsOn) {
    //         dependsOn = this.getTumblingWindowReferences(properties, parentTriggerName);
    //     }

    //     // Remove milliseconds from ISO string using split()
    //     const startTime: string = `${properties.startTime.toISOString().split('.')[0]}Z`;
    //     const endTime: string = properties.endTime ? `${properties.endTime.toISOString().split('.')[0]}Z` : undefined;

    //     return {
    //         frequency: properties.frequency,
    //         interval: properties.interval,
    //         startTime,
    //         endTime,
    //         delay: properties.delay,
    //         maxConcurrency: properties.maxConcurrency,
    //         retryPolicy,
    //         dependsOn
    //     };
    // }

    private blobEventsTriggerToResource(properties: EventTriggerProperties): Model.BlobEventsTriggerTypeProperties {
        return {
            blobPathBeginsWith: properties.blobPathBeginsWith,
            blobPathEndsWith: properties.blobPathEndsWith,
            ignoreEmptyBlobs: properties.ignoreEmptyBlobs,
            maxConcurrency: properties.maxConcurrency,
            scope: properties.scope,
            dmtsConnectionId: properties.dmtsConnectionId,
            storageAccountName: properties.storageAccountName,
            tenantId: properties.tenantId,
            events: properties.events
        };
    }

    // private customEventsTriggerToResource(properties: CustomEventsTriggerProperties): Model.CustomEventsTriggerTypeProperties {
    //     const customEventTrigger: Model.CustomEventsTriggerTypeProperties = {
    //         subjectEndsWith: properties.subjectEndsWith,
    //         subjectBeginsWith: properties.subjectBeginsWith,
    //         scope: properties.scope,
    //         tenantId: properties.tenantId,
    //         events: properties.events
    //     };

    //     if (properties.advancedFilters && properties.advancedFilters.length) {
    //         customEventTrigger.advancedFilters = properties.advancedFilters.filter((advancedFilter) => {
    //             return advancedFilter.key && advancedFilter.operatorType && (advancedFilter.value || advancedFilter.values);
    //         });
    //     }
    //     return customEventTrigger;
    // }

    // private chainingTriggerToResource(properties: ChainingTriggerProperties): Model.ChainingTriggerTypeProperties {
    //     return {
    //         dependsOn: properties.dependsOn.map((pipeline) => ReferenceAdapter.toResource(pipeline, this.context) as Model.PipelineReference),
    //         runDimension: properties.runDimension
    //     };
    // }

    // // eslint-disable-next-line @typescript-eslint/no-explicit-any
    // private rerunTumblingWindowTriggerToResource(properties: RerunTumblingWindowTriggerProperties): Model.RerunTumblingWindowTriggerTypeProperties {
    //     const parentTriggerReference = { referenceName: properties.parentTrigger, type: 'TriggerReference' };
    //     return {
    //         parentTrigger: parentTriggerReference,
    //         requestedStartTime: properties.requestedStartTime,
    //         requestedEndTime: properties.requestedEndTime,
    //         rerunConcurrency: properties.rerunConcurrency
    //     };
    // }

    // private retryPolicyToModel(retryPolicy?: Model.RetryPolicy): RetryPolicy | undefined {
    //     if (!retryPolicy) {
    //         return undefined;
    //     }
    //     return {
    //         count: retryPolicy.count,
    //         interval: retryPolicy.intervalInSeconds
    //     };
    // }

    // private getTriggerReferencesToTumblingWindow(twTriggerReferences: Model.TriggerDependency[], parentTriggerName: string): TriggerDependency[] {
    //     let triggerDependencies: TriggerDependency[] = [];
    //     if (twTriggerReferences) {
    //         triggerDependencies = twTriggerReferences.map((item) => {
    //             let trigger: Trigger = null;
    //             if (item.referenceTrigger) {
    //                 trigger = ReferenceAdapter.toProxyModel<Trigger>(Metamodel.modelFor(Trigger), item.referenceTrigger as Model.TriggerReference);
    //             } else {
    //                 const parentTriggerReference = { referenceName: parentTriggerName, type: 'TriggerReference' };
    //                 trigger = ReferenceAdapter.toProxyModel<Trigger>(Metamodel.modelFor(Trigger), parentTriggerReference as Model.TriggerReference);
    //             }
    //             const triggerDependency = new TriggerDependency(trigger, item.offset, item.size);
    //             return triggerDependency;
    //         });
    //     }
    //     return triggerDependencies;
    // }
}
